import type { TEndpointMethod } from '~/shared/interfaces/api';
import type { ILogin } from '~/shared/interfaces/auth';
import type { IPositionOnAdd } from '~/shared/interfaces/downloadPositions';
import type { IPartnerPositionOnAdd } from "~/shared/interfaces/search/products";
import type { IAccountSettings } from '~/shared/interfaces/account/sortingWarehouses';
import type { IOneClickRequest, IRememberMeRequest } from "~/shared/interfaces/search/product";
import type { IRegFormState } from "~/shared/interfaces/registration";
import type { IFastOrderForm } from "~/shared/interfaces/cart";
import type { IOrderDeliveryData } from "~/shared/interfaces/order";
import type { ICompany, TCompanyCreate } from '../interfaces/account';
import type { IAddress } from "~/shared/interfaces/account";
import type {TPersonalDataFields} from "~/shared/interfaces/account/personal";
import type { IProductQuery, IProductRequestForm } from '~/shared/interfaces/search/resultEmpty';

interface IEndpoints {
  auth: {
    refreshToken: TEndpointMethod,
    login: TEndpointMethod<ILogin>,
    logout: TEndpointMethod,
    registerIndividual: TEndpointMethod<IRegFormState>,
    registerEntity: TEndpointMethod<IRegFormState>,
    changePassword: TEndpointMethod<{
      password: string
      currentPassword: string
    }>
    resetPassword: TEndpointMethod<{
      email: string
    }>
    forgetPassword: TEndpointMethod<{
      token: string
      password: string
    }>
  }
  order: {
    getDeliveryAddress: TEndpointMethod,
    getDeliveryCompanies: TEndpointMethod,
    getOrderProduct: TEndpointMethod<{
      query: {
        posIds: string[]
      }
    }>
    getCityWarehouses: TEndpointMethod<{
      citySlug: string
    }>,
    sendOrder: TEndpointMethod<IOrderDeliveryData>
  }
  cart: {
    getCartChanges: TEndpointMethod
    addToCart1CProduct: TEndpointMethod<IPositionOnAdd[]>
    addToCartPartnerProduct: TEndpointMethod<IPartnerPositionOnAdd[]>
    editCartProduct: TEndpointMethod<{
      id: string
      comment: string | null
      count: number
    }>,
    deleteProductsFromCart: TEndpointMethod<string[]>
    deleteAllProductsFromCart: TEndpointMethod
    downloadPositionsList: TEndpointMethod<FormData>
    sendFastOrder: TEndpointMethod<IFastOrderForm>
  }
  account: {
    accountSettings: TEndpointMethod<IAccountSettings>
    setWarehousesSorting: TEndpointMethod<Record<string, {
      id: number
      orderWeight: number
      cityOrderWeight: number
      isShow: boolean
    }[]>>
    clearWarehousesSorting: TEndpointMethod
    deleteSearchHistoryQuery: TEndpointMethod<{
      article: string
      brand: string | null
      createdAt: string
    }>
    clearSearchHistory: TEndpointMethod
    updateStockCity: TEndpointMethod<{
      citySlug: string
    }>
    createDeliveryAddress: TEndpointMethod<IAddress>
    updateDeliveryAddress: TEndpointMethod<{
      body: IAddress
      id: string
    }>
    deleteDeliveryAddress: TEndpointMethod<{
      id: string
    }>
    createCompany: TEndpointMethod<TCompanyCreate>
    editCompany: TEndpointMethod<ICompany>
    deleteCompany: TEndpointMethod<number>
    updateCompanyIsMain: TEndpointMethod<{
      isMain: boolean
      companyId: number
    }>
    getSearchShortHistory: TEndpointMethod
    updateUserPersonalData: TEndpointMethod<Record<TPersonalDataFields, string>>
    managersRequest: TEndpointMethod<{
      text: string
    }>
  },
  inn: {
    findByInn: TEndpointMethod<string>,
  },
  banners: {
    getBanners: TEndpointMethod<{type: string}>,
  }
  product: {
    buyOneClickRequest: TEndpointMethod<IOneClickRequest>
    rememberMeRequest: TEndpointMethod<IRememberMeRequest>
  },
  brands: {
    getBrands: TEndpointMethod
  }
  suggests: {
    suggestAddress: TEndpointMethod<{
      address: string
    }>
  }
  mailing: {
    getSubscribe: TEndpointMethod<{
      email: string
    }>
  },
  search: {
    getProductRequestStatus: TEndpointMethod<IProductQuery>,
    sendProductRequest: TEndpointMethod<IProductRequestForm>
  }
}

const apiConfig: IEndpoints = {
  auth: {
    login: (body) => {
      return {
        method: 'POST',
        path: '/auth/login',
        body,
      }
    },
    logout: () => {
      return {
        method: 'POST',
        path: '/auth/logout'
      }
    },
    registerIndividual: (body) => {
      return {
        method: 'POST',
        path: '/auth/register/individual',
        body,
      }
    },
    registerEntity: (body) => {
      return {
        method: 'POST',
        path: '/auth/register/entity',
        body,
      }
    },
    refreshToken: () => {
      return {
        method: 'POST',
        path: '/auth/refresh',
      }
    },
    changePassword: (body) => {
      return {
        method: 'POST',
        path: '/auth/change/password',
        body,
      }
    },
    resetPassword: (body) => {
      return {
        method: 'POST',
        path: '/auth/reset-password',
        body
      }
    },
    forgetPassword: (query) => {
      return {
        method: 'POST',
        path: '/auth/forget-password',
        query
      }
    }
  },
  order: {
    getDeliveryAddress: () => {
      return {
        method: 'GET',
        path: '/account/deliveryAddress',
      }
    },
    getDeliveryCompanies: () => {
      return {
        method: 'GET',
        path: '/deliveryCompanies',
      }
    },
    getOrderProduct: (data) => {
      return {
        method: 'GET',
        path: `/cart/order`,
        query: data?.query
      }
    },
    getCityWarehouses: (data) => {
      return {
        method: 'GET',
        path: `/city/${data?.citySlug}/warehouses`,
      }
    },
    sendOrder: (body) => {
      return {
        method: 'POST',
        path: '/order',
        body
      }
    }
  },
  cart: {
    getCartChanges: () => {
      return {
        method: 'GET',
        path: '/cart/changes',
      }
    },
    addToCart1CProduct: (products) => {
      return {
        method: 'POST',
        path: '/cart',
        body: {
          products,
        },
      }
    },
    addToCartPartnerProduct: (products) => {
      return {
        method: 'POST',
        path: '/cart/partner',
        body: {
          products,
        },
      }
    },
    editCartProduct: (productData) => {
      return {
        method: 'PATCH',
        path: `/cart/${productData?.id}`,
        body: productData,
      }
    },
    downloadPositionsList: (body) => {
      return {
        method: 'POST',
        path: '/search/file',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        body,
      }
    },
    deleteProductsFromCart: (ids) => {
      return {
        method: 'DELETE',
        path: '/cart',
        body: {
          ids,
        },
      }
    },
    deleteAllProductsFromCart: () => {
      return {
        method: 'DELETE',
        path: '/cart/clear',
      }
    },
    sendFastOrder: (body) => {
      return {
        method: 'POST',
        path: '/order/fast-order',
        body
      }
    }
  },
  inn: {
    findByInn: (inn) => {
      return {
        method: 'GET',
        path: '/suggest/inn',
        query: {
          inn
        }
      }
    }
  },
  account: {
    managersRequest: (body) => {
      return {
        method: 'POST',
        path: '/manager/request/send',
        body
      }
    },
    createCompany: (body) => {
      return {
        method: 'POST',
        path: '/account/company',
        body
      }
    },
    editCompany: (body) => {
      return {
        method: 'PATCH',
        path: '/account/company/' + body?.id,
        body
      }
    },
    deleteCompany: (id) => {
      return {
        method: 'DELETE',
        path: '/account/company/' + id,
      }
    },
    updateCompanyIsMain: (query) => {
      return {
        method: 'PUT',
        path: '/account/company/setIsMain',
        query
      }
    },
    accountSettings: (body) => {
      return {
        method: 'PATCH',
        path: '/account/settings',
        body
      }
    },
    setWarehousesSorting: (body) => {
      return {
        method: 'PATCH',
        path: '/account/warehouses',
        body,
      }
    },
    clearWarehousesSorting: () => {
      return {
        method: 'GET',
        path: '/account/warehouses/clear'
      }
    },
    deleteSearchHistoryQuery: (query) => {
      return {
        method: 'DELETE',
        path: `/account/history`,
        query
      }
    },
    clearSearchHistory: () => {
      return {
        method: 'DELETE',
        path: '/account/historyClear',
      }
    },
    updateStockCity: (city) => {
      return {
        method: 'PATCH',
        path: '/account/stockCity',
        body: city
      }
    },
    createDeliveryAddress: (body) => {
      return {
        method: 'POST',
        path: '/account/deliveryAddress',
        body
      }
    },
    updateDeliveryAddress: (data) => {
      return {
        method: 'PUT',
        path: `/account/deliveryAddress/${data?.id}`,
        body: data?.body
      }
    },
    deleteDeliveryAddress: (data) => {
      return {
        method: 'DELETE',
        path: `/account/deliveryAddress/${data?.id}`,
      }
    },
    getSearchShortHistory: () => {
      return {
        method: 'GET',
        path: '/account/shortHistory',
      }
    },
    updateUserPersonalData: (body) => {
      return {
        method: 'PATCH',
        path: '/account',
        body
      }
    }
  },
  banners: {
    getBanners: (data) => {
      return {
        method: 'GET',
        path: `/banners/${data?.type}`,
      }
    },
  },
  product: {
    buyOneClickRequest: (body) => {
      return {
        method: 'POST',
        path: '/order/one-click',
        body
      }
    },
    rememberMeRequest: (body) => {
      return {
        method: 'POST',
        path: '/remember-me',
        body
      }
    }
  },
  suggests: {
    suggestAddress: (query) => {
      return {
        method: 'GET',
        path: `/suggest/address`,
        query
      }
    }
  },
  mailing: {
    getSubscribe: (body) => {
      return {
        method: 'POST',
        path: '/mailing-list',
        body
      }
    }
  },
  brands: {
    getBrands: () => {
      return {
        method: 'GET',
        path: '/index/brandsSection',
      }
    },
  },
  search: {
    getProductRequestStatus: (query) => {
      return {
        method: 'GET',
        path: '/app/request/status',
        query
      }
    },
    sendProductRequest: (formData) => {
      return {
        method: 'POST',
        path: '/app/request/send',
        body: formData
      }
    }
  }
}

export default apiConfig;
